<template>
    <v-form>
        <v-card>
            <v-toolbar dark color="primary">
                <v-toolbar-title>{{ formTitle }}</v-toolbar-title>
                <slot name="header"></slot>
            </v-toolbar>
            <v-card-text>
                <v-container grid-list-md>
                    <v-layout wrap>
                        <v-flex xs12>
                            <v-menu
                                v-model="resetDateDialog"
                                :close-on-content-click="false"
                                max-width="290"
                            >
                                <template v-slot:activator="{ on }">
                                    <v-text-field
                                        :value="formattedResetDate"
                                        label="When was the meter reset?"
                                        readonly
                                        v-validate="resetDateRequired ? 'required' : ''"
                                        :error-messages="errors.first('reset date')"
                                        name="reset date"
                                        v-on="on"
                                        :required="resetDateRequired"
                                        append-icon="event"
                                        @click:append="resetDateDialog = true"
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    v-model="value.resetDate"
                                    @change="resetDateDialog = false; reValidate('hours before reset', value.hoursBefore); reValidate('km before reset', value.kilometresBefore)"
                                    no-title
                                    :max="today"
                                ></v-date-picker>
                            </v-menu>
                        </v-flex>
                        <v-flex>What were the meter readings before and after the reset?</v-flex>
                        <v-flex xs6>
                            <decimal
                                v-model="value.hoursBefore"
                                placeholder="e.g. 13465"
                                v-validate="this.editing ? 'required' : 'required|in-range-hr'"
                                label="Hours (before reset)"
                                name="hours before reset"
                                :error-messages="errors.first('hours before reset')"
                                :precision="1"
                            ></decimal>
                        </v-flex>
                        <v-flex xs6>
                            <decimal
                                v-model="value.hoursAfter"
                                placeholder="e.g. 0"
                                v-validate="'required'"
                                label="Hours (after reset)"
                                name="hours after reset"
                                :error-messages="errors.first('hours after reset')"
                                :precision="1"
                            ></decimal>
                        </v-flex>
                        <v-flex xs6>
                            <decimal
                                v-model="value.kilometresBefore"
                                placeholder="e.g. 120342"
                                v-validate="this.editing ? 'required' : 'required|in-range-km'"
                                label="Kilometres (before reset)"
                                name="km before reset"
                                :error-messages="errors.first('km before reset')"
                                :precision="1"
                            ></decimal>
                        </v-flex>
                        <v-flex xs6>
                            <decimal
                                v-model="value.kilometresAfter"
                                placeholder="e.g. 0"
                                v-validate="'required'"
                                label="Kilometres (after reset)"
                                name="km after reset"
                                :error-messages="errors.first('km after reset')"
                                :precision="1"
                            ></decimal>
                        </v-flex>
                        <v-flex xs12 v-if="userIsFleetManager">
                            <v-checkbox
                                v-model="disableMeterValidation"
                                @change="$validator.validate()"
                            >
                                <template v-slot:label>Disable Meter Validation</template>
                            </v-checkbox>
                        </v-flex>
                    </v-layout>
                </v-container>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click="close">Cancel</v-btn>
                <v-btn
                    color="primary"
                    :disabled="disableSubmit"
                    @click="submit"
                >{{ !this.editing ? 'Save Changes' : 'Update' }}</v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>
<script>
import moment from 'moment';
import FormValidation from '@/components/FormValidation.vue';
import Decimal from '@/components/Decimal.vue';
import Date from '@/components/Date.vue';
import { Event } from '../../../../plugins/bus';
import CONFIG from '../../../../config';
import { DateFormat } from '../../../../plugins/date-filter';
import MetricToleranceCheck from '@/components/MetricToleranceCheck';

export default {
    props: {
        value: {
            required: true
        }
    },
    components: {
        Decimal,
        Date
    },
    mixins: [FormValidation, MetricToleranceCheck],
    computed: {
        formTitle() {
            return !this.editing ? 'Add Meter Reset' : 'Edit Meter Reset';
        },
        formattedResetDate() {
            return this.value.resetDate ? moment(this.value.resetDate).format(DateFormat.DefaultDisplay) : '';
        },
        resetDateRequired() {
            return !CONFIG.featureToggles.inferMeterResetDatesEnabled;
        },
        editing() {
            return !!this.value.id;
        }
    },
    methods: {
        submitForm() {
            this.addOrUpdate();
        },
        addOrUpdate() {
            if (!this.editing) {
                this.$bus.$emit(Event.UnitMeterResetPendingAdd, { ...this.value, id: this.id });
            } else {
                this.$bus.$emit(Event.UnitMeterResetPendingUpdate, { ...this.value, id: this.id });
            }
        },
        close() {
            this.$emit('close');
            this.disableMeterValidation = false;
        },
        reValidate(formInput, modelField) {
            if (modelField == null || modelField == undefined || modelField == '') {
                return;
            }
            this.$validator.validate(formInput, modelField);
        }
    },
    data() {
        return {
            id: '',
            today: moment().format(DateFormat.Canonical),
            resetDateDialog: false,
            disableMeterValidation: false
        };
    },
    created() {
        const isInRangeHr = async value => {
            if (this.disableMeterValidation) {
                return true;
            }
            return await this.checkMetricIsInRange(this.value.unitId, 'Hours', value, this.value.resetDate);
        };
        const isInRangeKm = async value => {
            if (this.disableMeterValidation) {
                return true;
            }
            return await this.checkMetricIsInRange(this.value.unitId, 'Kilometres', value, this.value.resetDate);
        };

        this.$validator.extend('in-range-hr', {
            validate: isInRangeHr,
            getMessage: (field, params, data) => data.message
        });
        this.$validator.extend('in-range-km', {
            validate: isInRangeKm,
            getMessage: (field, params, data) => data.message
        });
    },
    watch: {
        value: {
            async handler(val) {
                if (val.id) {
                    this.id = val.id;
                } else {
                    this.id = this.$uuid.v1();
                }
            },
            immediate: true
        }
    }
};
</script>
